import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { ElasticModal } from 'basic-components';

import Body from './Body';
import Footer from './Footer';
import Header from './Header';

import parser from 'cron-parser';

import './Modal.scss';

const defaultCron = "0 0 14 ? * MON,TUE,WED,THU,FRI"

const assignIfExist = (fields, value, name) => {
  if(value !== undefined && value !== "") {
    fields[name] = value;
  }
}

const cronToObject = cron => {
  const interval = parser.parseExpression(cron);
  const { hour, minute, dayOfWeek, dayOfMonth } = Object.assign({}, interval.fields)
  return { hour, minute, dayOfWeek, dayOfMonth }
}

const cronFieldToString = field => Array.isArray(field) ? field.join(",") : field

const genericToCronField = (field, defaultField, number, increment = 0, withArray = false) => {
  if(field === undefined || field === "") {
    if(defaultField && Array.isArray(defaultField)) {
      return defaultField.toUnique()
    }
    return undefined;
  }
  if(field === "?") {
    return undefined;
  }
  if(field === "*") {
    return [...Array(number).keys()].map(k => Number(k) + increment).toUnique()
  }
  if(String(field).indexOf("/") > -1 && withArray) {
    const localNumber = Number(String(field).slice(2))
    let curr = 0
    const array = [0]
    while(curr + localNumber < number) {
      array.push(curr + localNumber)
      curr += localNumber
    }
    return array.toUnique()
  }
  return field.split(",").map(v => Number(v)).toUnique()
}

const hourToCronField = (hour, defaultHour) => genericToCronField(hour, defaultHour, 24, 0, true)
const minuteToCronField = (minute, defaultMinute) => genericToCronField(minute, defaultMinute, 60, 0, true)
const dayOfWeekToCronField = (dayOfWeek, defaultDayOfWeek) => genericToCronField(dayOfWeek, defaultDayOfWeek, 7, 0, false)
const dayOfMonthToCronField = (dayOfMonth, defaultDayOfMonth) => genericToCronField(dayOfMonth, defaultDayOfMonth, 31, 1, false)

const checkForQuestionMark = (variable, index, cron, almostFinal) => {
  if(variable === "?" || variable === "" || (variable === undefined && cron.split(" ")[index] === "?")) {
    almostFinal[index] = "?"
  }
}

const checkForSlashAndStar = (variable, index, cron, almostFinal) => {
  if(String(variable).indexOf("/") > -1) {
    almostFinal[index] = variable
  } else if(variable === "*") {
    almostFinal[index] = variable
  }
}

const checkIfShouldBeSlash = (field = "", maxNumber) => {
  if(field === "*" || field === "" || field[0] !== 0 || field[1] === undefined) {
    return field;
  }
  let isAnyWrong = false, correctCount = 0
  const first = field[1], countShouldBe = Math.ceil(maxNumber / Number(first))
  field.forEach(item => {
    if(Number(item)%Number(first) !== 0 && item !== 0) {
      isAnyWrong = true
    } else {
      correctCount++;
    }
  })
  if(!isAnyWrong && correctCount === countShouldBe) {
    return `0/${first}`
  }
  return field;
}

const replaceDaysOfWeek = almostFinal => {
  almostFinal[5] = almostFinal[5]
    .replace("0", "SUN")
    .replace("1", "MON")
    .replace("2", "TUE")
    .replace("3", "WED")
    .replace("4", "THU")
    .replace("5", "FRI")
    .replace("6", "SAT")
}

const Modal = ({ closeModal, defaultValue = defaultCron, onSubmit }) => {
  const [activeStep, setActiveStep] = useState(),
        [cron, setCron] = useState(defaultValue || defaultCron),
        [hour, setHour] = useState(),
        [minute, setMinute] = useState(),
        [dayOfWeek, setDayOfWeek] = useState(),
        [dayOfMonth, setDayOfMonth] = useState(),
        onSubmitLocal = useCallback(() => {
          onSubmit(cron)
          closeModal()
        }, [cron, onSubmit, closeModal]),
        varsFromCron = useCallback(cron => {
          const { hour, minute, dayOfWeek, dayOfMonth } = cronToObject(cron)
          setHour(cronFieldToString(cron.split(" ")[2] === "*" ? "*" : checkIfShouldBeSlash(hour, 24)))
          setMinute(cronFieldToString(checkIfShouldBeSlash(minute, 60)))
          setDayOfWeek(cron.split(" ")[5] === "?" ? "?" : cronFieldToString(dayOfWeek.length === 7 ? "*" : dayOfWeek))
          setDayOfMonth(cron.split(" ")[3] === "?" ? "?" : cronFieldToString(dayOfMonth.length === 31 ? "*" : dayOfMonth))
        }, []),
        footer = useMemo(() => (
          <Footer
              cron={cron}
              onCancel={closeModal}
              onSubmit={onSubmitLocal}
          />
        ), [cron, closeModal, onSubmitLocal])

  useEffect(() => {
    try {
      parser.parseExpression(cron); // to catch unexpected errors
      varsFromCron(cron)
    } catch(e) {
      console.error(e)
      varsFromCron(defaultCron)
    }
  }, [cron, varsFromCron])

  useEffect(() => {
    setCron(cron => {
      try {
        const interval = parser.parseExpression(cron);
        const fields = JSON.parse(JSON.stringify(interval.fields))
        assignIfExist(fields, hourToCronField(hour, interval.fields.hour), "hour")
        assignIfExist(fields, minuteToCronField(minute, interval.fields.minute), "minute")
        assignIfExist(fields, dayOfWeekToCronField(dayOfWeek, interval.fields.dayOfWeek), "dayOfWeek")
        assignIfExist(fields, dayOfMonthToCronField(dayOfMonth, interval.fields.dayOfMonth), "dayOfMonth")
        const modifiedInterval = parser.fieldsToExpression(fields),
              seconds = cron.split(" ")[0],
              almostFinal = (`${seconds} ${modifiedInterval.stringify()}`).split(" ")
        checkForQuestionMark(dayOfWeek, 5, cron, almostFinal)
        checkForQuestionMark(dayOfMonth, 3, cron, almostFinal)
        checkForSlashAndStar(hour, 2, cron, almostFinal)
        checkForSlashAndStar(minute, 1, cron, almostFinal)
        replaceDaysOfWeek(almostFinal)
        return almostFinal.join(" ")
      } catch(e) {
        console.error(e)
        return defaultCron;
      }
    })
  }, [hour, minute, dayOfWeek, dayOfMonth])

  return (
    <ElasticModal
        className="cron-creator"
        closeOnBackground={false}
        footer={footer}
        onClose={closeModal}
        title="Schedule"
    >
      <Header
          activeStep={activeStep}
          dayOfMonth={dayOfMonth}
          dayOfWeek={dayOfWeek}
          hour={hour}
          minute={minute}
          setActiveStep={setActiveStep}
      />
      <Body
          activeStep={activeStep}
          dayOfMonth={dayOfMonth}
          dayOfWeek={dayOfWeek}
          hour={hour}
          minute={minute}
          setDayOfMonth={setDayOfMonth}
          setDayOfWeek={setDayOfWeek}
          setHour={setHour}
          setMinute={setMinute}
      />
    </ElasticModal>
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  onSubmit: PropTypes.func.isRequired
}

export default memo(Modal)